/**
 * TanStack Query hooks for Deals - Supabase Edition
 *
 * Features:
 * - Real Supabase API calls
 * - Optimistic updates for instant UI feedback
 * - Automatic cache invalidation
 * - Ready for Realtime integration
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  queryKeys,
  DEALS_VIEW_KEY,
  DEALS_BY_BOARD_KEY,
  DEALS_BY_BOARD_PREFIX,
} from '../index';
import { dealsService, contactsService, companiesService } from '@/lib/supabase';
import { getClient } from '@/lib/supabase/client';
import { useAuth } from '@/context/AuthContext';
import type { Deal, DealView, DealItem, Contact } from '@/types';

// ============ QUERY HOOKS ============

// Stable selector factory — must be at module level to avoid new reference per render
const makeSelectByBoard = (boardId: string) => (data: DealView[]) => {
  if (!boardId || boardId.startsWith('temp-')) return [];
  return data.filter(d => d.boardId === boardId);
};

interface DealEnrichedRow {
  id: string;
  organization_id: string;
  board_id: string | null;
  stage_id: string | null;
  status: string | null;
  contact_id: string | null;
  client_company_id: string | null;
  title: string;
  value: number | null;
  probability: number | null;
  priority: string | null;
  is_won: boolean | null;
  is_lost: boolean | null;
  closed_at: string | null;
  ai_summary: string | null;
  loss_reason: string | null;
  tags: string[] | null;
  last_stage_change_date: string | null;
  custom_fields: Record<string, unknown> | null;
  owner_id: string | null;
  reactivation_sequence: number | null;
  reactivated_from_deal_id: string | null;
  created_at: string;
  updated_at: string;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  contact_classification: string | null;
  contact_is_ad_lead: boolean | null;
  contact_is_corretor: boolean | null;
  contact_has_trade_in: boolean | null;
  ad_name: string | null;
  ad_campaign_name: string | null;
  ad_adset_name: string | null;
  ad_platform: string | null;
  ad_attribution_status: string | null;
  ad_clicked_at: string | null;
  company_name: string | null;
  stage_label: string | null;
  last_message_preview: string | null;
  unread_count: number | null;
  last_message_direction: string | null;
  last_message_at: string | null;
  rotting_threshold_days: number | null;
  rotting_days: number | null;
  conversation_id: string | null;
  qual_stage: string | null;
  qual_qualified_by_type: string | null;
  ai_review_pending: boolean | null;
}

export const mapEnrichedRowToView = (row: DealEnrichedRow): DealView => {
  const stageStatus = row.stage_id || row.status || '';
  const unread = row.unread_count ?? 0;
  const thresholdDays = row.rotting_threshold_days ?? 30;
  const lastMsgAt = row.last_message_at ? new Date(row.last_message_at).getTime() : null;
  const isWithinThreshold = lastMsgAt !== null
    ? (Date.now() - lastMsgAt) < thresholdDays * 24 * 60 * 60 * 1000
    : false;
  return {
    id: row.id,
    organizationId: row.organization_id,
    title: row.title,
    value: row.value ?? 0,
    probability: row.probability ?? 0,
    status: stageStatus,
    isWon: row.is_won ?? false,
    isLost: row.is_lost ?? false,
    closedAt: row.closed_at ?? undefined,
    priority: (row.priority as Deal['priority']) || 'medium',
    boardId: row.board_id ?? '',
    contactId: row.contact_id ?? '',
    clientCompanyId: row.client_company_id ?? undefined,
    companyId: row.client_company_id ?? '',
    aiSummary: row.ai_summary ?? undefined,
    lossReason: row.loss_reason ?? undefined,
    tags: row.tags ?? [],
    lastStageChangeDate: row.last_stage_change_date ?? undefined,
    customFields: (row.custom_fields ?? {}) as Record<string, unknown>,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    items: [],
    owner: { name: 'Sem Dono', avatar: '' },
    ownerId: row.owner_id ?? undefined,
    reactivationSequence: row.reactivation_sequence ?? 1,
    reactivatedFromDealId: row.reactivated_from_deal_id ?? null,
    // Enriched fields
    companyName: row.company_name || 'Sem empresa',
    contactName: row.contact_name || 'Sem contato',
    contactEmail: row.contact_email || '',
    contactPhone: row.contact_phone || '',
    stageLabel: row.stage_label || 'Estágio não identificado',
    adName: row.ad_name ?? null,
    adCampaignName: row.ad_campaign_name ?? null,
    adAdsetName: row.ad_adset_name ?? null,
    adPlatform: row.ad_platform ?? null,
    adAttributionStatus: row.ad_attribution_status ?? null,
    adClickedAt: row.ad_clicked_at ?? null,
    contactClassification: row.contact_classification ?? null,
    contactIsAdLead: row.contact_is_ad_lead ?? null,
    contactIsCorretor: row.contact_is_corretor ?? null,
    contactHasTradeIn: row.contact_has_trade_in ?? null,
    lastMessagePreview: row.last_message_preview ?? null,
    unreadCount: unread,
    // "Aguardando" pisca enquanto a última mensagem é do lead e o card não está
    // estagnado. Não usamos `unread > 0` porque abrir o Inbox dispara markAsRead
    // automático e zera o unread — mas o lead continua aguardando uma resposta
    // efetiva (responder é gesto ativo, abrir é passivo).
    needsAttention: row.last_message_direction === 'inbound' && isWithinThreshold,
    rottingDays: row.rotting_days ?? 10,
    lastMessageAt: row.last_message_at ?? undefined,
    lastMessageDirection: row.last_message_direction ?? null,
    conversationId: row.conversation_id ?? undefined,
    qualStage: (row.qual_stage as 'frio' | 'morno' | 'quente' | 'perdido' | null) ?? null,
    qualQualifiedByType: (row.qual_qualified_by_type as 'ai' | 'manual' | null) ?? null,
    aiReviewPending: row.ai_review_pending ?? false,
  };
};

// ============ CACHE HELPERS (Fase 1B/1C) ============

/** Cache do useInfiniteQuery (Fase 1C — DEALS_BY_STAGE_KEY) */
type InfiniteDealCache = { pages: DealView[][]; pageParams: unknown[] };

/** Detecta se uma queryKey é cache infinito (stage) pelo segmento 'byStage'. */
function isStageKey(key: readonly unknown[]): boolean {
  return key.some(seg => seg === 'byStage');
}

/**
 * Aplica um patcher a TODOS os caches de deals ativos.
 *
 * Cobre:
 * - DEALS_VIEW_KEY (full org — usado por dashboard, inbox, cockpit, decisions)
 * - DEALS_BY_BOARD_KEY(*) (kanban Fase 1B — array simples)
 * - DEALS_BY_STAGE_KEY(*, *) (kanban Fase 1C — useInfiniteQuery, aplica em cada page)
 *
 * Use pra UPDATE/DELETE/map/filter. Pra INSERT use `insertDealInCaches`
 * (precisa escolher o cache correto pelo `newDeal.boardId/stageId`).
 */
export function applyDealPatchToCaches(
  queryClient: QueryClient,
  patcher: (deals: DealView[]) => DealView[]
): void {
  // 1. Full org cache
  const fullCache = queryClient.getQueryData<DealView[]>(DEALS_VIEW_KEY);
  if (fullCache !== undefined) {
    queryClient.setQueryData<DealView[]>(DEALS_VIEW_KEY, patcher);
  }

  // 2. Per-board + per-stage caches (mesmo prefix 'byBoard')
  const caches = queryClient.getQueriesData({ queryKey: DEALS_BY_BOARD_PREFIX });
  caches.forEach(([key, data]) => {
    if (data === undefined || data === null) return;

    if (isStageKey(key)) {
      // Stage cache: useInfiniteQuery, aplica patcher em cada page
      const infinite = data as InfiniteDealCache;
      if (!Array.isArray(infinite.pages)) return;
      const newPages = infinite.pages.map(page => patcher(page));
      queryClient.setQueryData<InfiniteDealCache>(key, {
        pages: newPages,
        pageParams: infinite.pageParams,
      });
    } else {
      // Board cache (Fase 1B): array simples
      if (!Array.isArray(data)) return;
      queryClient.setQueryData<DealView[]>(key, patcher);
    }
  });
}

/**
 * Atualiza um deal que mudou de board sem recarregar listas inteiras.
 *
 * Quando `boardId` muda, apenas fazer merge do objeto deixa o card preso no
 * cache do board antigo. Este helper remove o deal de qualquer cache cujo
 * board na queryKey não seja mais o board atual, e atualiza o cache full.
 */
function applyDealUpdateToCaches(
  queryClient: QueryClient,
  dealId: string,
  updates: Partial<Deal>,
): void {
  const updatedAt = new Date().toISOString();
  const patchDeal = (deal: DealView): DealView => (
    deal.id === dealId
      ? { ...deal, ...updates, updatedAt } as DealView
      : deal
  );

  const targetBoardId = updates.boardId;
  if (!targetBoardId) {
    applyDealPatchToCaches(queryClient, (old = []) => old.map(patchDeal));
    return;
  }

  const fullCache = queryClient.getQueryData<DealView[]>(DEALS_VIEW_KEY);
  if (fullCache !== undefined) {
    queryClient.setQueryData<DealView[]>(DEALS_VIEW_KEY, (old = []) => old.map(patchDeal));
  }

  const caches = queryClient.getQueriesData({ queryKey: DEALS_BY_BOARD_PREFIX });
  caches.forEach(([key, data]) => {
    if (data === undefined || data === null) return;

    const queryKey = Array.isArray(key) ? key : [];
    const byBoardIndex = queryKey.findIndex((segment) => segment === 'byBoard');
    const cacheBoardId = byBoardIndex >= 0 ? String(queryKey[byBoardIndex + 1] ?? '') : '';
    if (!cacheBoardId) return;

    const patchBoardList = (old: DealView[] = []) =>
      old.flatMap((deal) => {
        if (deal.id !== dealId) return [deal];
        const next = patchDeal(deal);
        return next.boardId === cacheBoardId ? [next] : [];
      });

    if (isStageKey(queryKey)) {
      const infinite = data as InfiniteDealCache;
      if (!Array.isArray(infinite.pages)) return;
      queryClient.setQueryData<InfiniteDealCache>(key, {
        pages: infinite.pages.map((page) => patchBoardList(page)),
        pageParams: infinite.pageParams,
      });
      return;
    }

    if (!Array.isArray(data)) return;
    queryClient.setQueryData<DealView[]>(key, patchBoardList);
  });
}

/**
 * Insere um novo deal nos caches relevantes (sem duplicar).
 *
 * - DEALS_VIEW_KEY (se ativo): prepend
 * - DEALS_BY_BOARD_KEY(newDeal.boardId) (se ativo): prepend
 * - DEALS_BY_STAGE_KEY(newDeal.boardId, newDeal.status) (se ativo): prepend
 *   na primeira page do useInfiniteQuery
 *
 * Caches de OUTROS boards/stages não são tocados.
 * Dedup por `id`.
 */
export function insertDealInCaches(
  queryClient: QueryClient,
  newDeal: DealView
): void {
  const dedupAndPrepend = (old: DealView[] = []) => {
    if (old.some(d => d.id === newDeal.id)) return old;
    return [newDeal, ...old];
  };

  // 1. Full org cache
  const fullCache = queryClient.getQueryData<DealView[]>(DEALS_VIEW_KEY);
  if (fullCache !== undefined) {
    queryClient.setQueryData<DealView[]>(DEALS_VIEW_KEY, dedupAndPrepend);
  }

  // 2. Cache do board do deal (Fase 1B)
  if (newDeal.boardId) {
    const boardKey = DEALS_BY_BOARD_KEY(newDeal.boardId);
    const boardCache = queryClient.getQueryData<DealView[]>(boardKey);
    if (boardCache !== undefined) {
      queryClient.setQueryData<DealView[]>(boardKey, dedupAndPrepend);
    }
  }

  // 3. Cache do stage do deal (Fase 1C — useInfiniteQuery)
  // O `status` do DealView contém o stage_id atual (ver mapEnrichedRowToView).
  if (newDeal.boardId && newDeal.status) {
    const stageKey = [...queryKeys.deals.all, 'byBoard', newDeal.boardId, 'byStage', newDeal.status, 'view'] as const;
    const stageCache = queryClient.getQueryData<InfiniteDealCache>(stageKey);
    if (stageCache !== undefined && Array.isArray(stageCache.pages)) {
      const [firstPage, ...restPages] = stageCache.pages;
      const newFirstPage = dedupAndPrepend(firstPage ?? []);
      queryClient.setQueryData<InfiniteDealCache>(stageKey, {
        pages: [newFirstPage, ...restPages],
        pageParams: stageCache.pageParams,
      });
    }
  }
}

/**
 * Captura snapshot de TODOS os caches de deals (full + per-board + per-stage).
 * Usado por mutations pra rollback em onError.
 */
export function snapshotDealCaches(queryClient: QueryClient): {
  full: DealView[] | undefined;
  caches: Array<{ key: readonly unknown[]; data: unknown }>;
} {
  const full = queryClient.getQueryData<DealView[]>(DEALS_VIEW_KEY);
  const caches = queryClient
    .getQueriesData({ queryKey: DEALS_BY_BOARD_PREFIX })
    .filter(([, data]) => data !== undefined && data !== null)
    .map(([key, data]) => ({ key, data }));
  return { full, caches };
}

/**
 * Restaura um snapshot capturado por `snapshotDealCaches`. Usado em onError.
 */
export function restoreDealCachesSnapshot(
  queryClient: QueryClient,
  snapshot: ReturnType<typeof snapshotDealCaches>
): void {
  if (snapshot.full !== undefined) {
    queryClient.setQueryData(DEALS_VIEW_KEY, snapshot.full);
  }
  snapshot.caches.forEach(({ key, data }) => {
    queryClient.setQueryData(key, data);
  });
}

/**
 * Base queryFn para DEALS_VIEW_KEY.
 *
 * Lê da view `deals_enriched` (deals + contact + company + stage + last
 * conversation em uma única query). Antes eram 5 queries em waterfall com
 * 1800+ deals, agora é 1 SELECT — ~70% de redução de latência.
 *
 * Chunk de 1000 por segurança do cap do PostgREST; a view não tem .limit()
 * implícito então paginamos via range pra garantir que o cap de 1000 do
 * default REST count header não corte silenciosamente.
 */
export const dealsViewQueryFn = async (
  { signal }: { signal?: AbortSignal } = {}
): Promise<DealView[]> => {
  const sb = getClient();
  const PAGE_SIZE = 1000;
  const allRows: DealEnrichedRow[] = [];
  let offset = 0;

  // Chama RPC SECURITY DEFINER que filtra por get_user_org_id() server-side.
  // View `deals_enriched` não é acessível diretamente (RLS via RPC wrapper).
  while (true) {
    let q = sb.rpc('list_deals_enriched_for_user', {
      p_offset: offset,
      p_limit: PAGE_SIZE,
    });
    if (signal) q = q.abortSignal(signal);
    const { data, error } = await q;
    if (error) throw error;
    const rows = (data || []) as DealEnrichedRow[];
    allRows.push(...rows);
    if (rows.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
    if (offset >= 10000) break;
  }

  return allRows.map(mapEnrichedRowToView);
};

export interface DealsFilters {
  boardId?: string;
  /** Stage id (UUID) do board_stages */
  status?: string;
  search?: string;
  minValue?: number;
  maxValue?: number;
}

/**
 * Hook to fetch all deals with optional filters
 * Waits for auth to be ready before fetching to ensure RLS works correctly
 */
export const useDeals = (filters?: DealsFilters, options?: { enabled?: boolean }) => {
  const { user, loading: authLoading } = useAuth();
  const externalEnabled = options?.enabled ?? true;

  return useQuery({
    queryKey: filters
      ? queryKeys.deals.list(filters as Record<string, unknown>)
      : queryKeys.deals.lists(),
    queryFn: async ({ signal }) => {
      const { data, error } = await dealsService.getAll({ signal });
      if (error) throw error;

      let deals = data || [];

      // Apply client-side filters
      if (filters) {
        deals = deals.filter(deal => {
          if (filters.boardId && deal.boardId !== filters.boardId) return false;
          if (filters.status && deal.status !== filters.status) return false;
          if (filters.minValue && deal.value < filters.minValue) return false;
          if (filters.maxValue && deal.value > filters.maxValue) return false;
          if (filters.search) {
            const search = filters.search.toLowerCase();
            if (!(deal.title || '').toLowerCase().includes(search)) return false;
          }
          return true;
        });
      }

      return deals;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !authLoading && !!user && externalEnabled,
  });
};

/**
 * Hook to fetch all deals with enriched company/contact data (DealView)
 * Waits for auth to be ready before fetching to ensure RLS works correctly
 */
export const useDealsView = (filters?: DealsFilters) => {
  const { user, loading: authLoading } = useAuth();

  return useQuery<DealView[]>({
    queryKey: filters
      ? [...queryKeys.deals.list(filters as Record<string, unknown>), 'view']
      : [...queryKeys.deals.lists(), 'view'],
    queryFn: async ({ signal }) => {
      const deals = await dealsViewQueryFn({ signal });
      if (!filters) return deals;

      return deals.filter(deal => {
        if (filters.boardId && deal.boardId !== filters.boardId) return false;
        if (filters.status && deal.status !== filters.status) return false;
        if (filters.minValue && deal.value < filters.minValue) return false;
        if (filters.maxValue && deal.value > filters.maxValue) return false;
        if (filters.search) {
          const search = filters.search.toLowerCase();
          if (
            !(deal.title || '').toLowerCase().includes(search) &&
            !(deal.companyName || '').toLowerCase().includes(search)
          )
            return false;
        }
        return true;
      });
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !authLoading && !!user, // Only fetch when auth is ready
  });
};

/**
 * Hook to fetch a single deal by ID
 */
export const useDeal = (id: string | undefined) => {
  const { user, loading: authLoading } = useAuth();
  return useQuery({
    queryKey: queryKeys.deals.detail(id || ''),
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await dealsService.getById(id);
      if (error) throw error;
      return data;
    },
    enabled: !authLoading && !!user && !!id,
  });
};

/**
 * QueryFn base pra DEALS_BY_STAGE_KEY (Fase 1C — estilo Kommo).
 *
 * Carrega uma página de deals de um stage específico via RPC
 * `list_deals_enriched_for_board(p_stage_id=stageId, p_offset, p_limit)`.
 */
export const dealsByStageQueryFn = async (
  boardId: string,
  stageId: string,
  offset: number,
  options?: { signal?: AbortSignal; search?: string }
): Promise<DealView[]> => {
  const sb = getClient();
  let q = sb.rpc('list_deals_enriched_for_board', {
    p_board_id: boardId,
    p_stage_id: stageId,
    p_offset: offset,
    p_limit: 30,
    ...(options?.search ? { p_search: options.search } : {}),
  });
  if (options?.signal) q = q.abortSignal(options.signal);
  const { data, error } = await q;
  if (error) throw error;
  const rows = (data || []) as DealEnrichedRow[];
  return rows.map(mapEnrichedRowToView);
};

/**
 * QueryFn base pra DEALS_BY_BOARD_KEY (Fase 1B).
 *
 * Carrega todos os deals do board atual via RPC `list_deals_enriched_for_board`.
 * Páginas de 1000; cap defensivo de 5000 (boards maiores que isso são edge case).
 */
export const dealsByBoardQueryFn = async (
  boardId: string,
  options?: { signal?: AbortSignal; search?: string }
): Promise<DealView[]> => {
  const sb = getClient();
  const PAGE_SIZE = 1000;
  const allRows: DealEnrichedRow[] = [];
  let offset = 0;

  while (true) {
    let q = sb.rpc('list_deals_enriched_for_board', {
      p_board_id: boardId,
      p_stage_id: null,
      p_offset: offset,
      p_limit: PAGE_SIZE,
      ...(options?.search ? { p_search: options.search } : {}),
    });
    if (options?.signal) q = q.abortSignal(options.signal);
    const { data, error } = await q;
    if (error) throw error;
    const rows = (data || []) as DealEnrichedRow[];
    allRows.push(...rows);
    if (rows.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
    if (offset >= 5000) break;
  }

  return allRows.map(mapEnrichedRowToView);
};

/**
 * Hook to fetch deals by board (for Kanban view) - Returns DealView[]
 *
 * Fase 1B: usa RPC `list_deals_enriched_for_board` com filtro server-side
 * por `board_id`. Cache próprio em `DEALS_BY_BOARD_KEY(boardId)` — não
 * compartilha mais com `useDealsView` (full org). Realtime e mutations
 * escrevem em AMBOS via helper `applyDealPatchToCaches`.
 *
 * Payload típico: ~600KB por board em vez de ~6MB da org inteira.
 */
export const useDealsByBoard = (boardId: string, searchTerm?: string) => {
  const { user, session, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const search = searchTerm && searchTerm.trim().length >= 2 ? searchTerm.trim() : undefined;
  return useQuery<DealView[]>({
    queryKey: [...DEALS_BY_BOARD_KEY(boardId), search ?? ''],
    queryFn: async ({ signal }) => {
      const rpcResult = await dealsByBoardQueryFn(boardId, { signal, search });

      // Race condition fix: preserva deals injetados pelo Realtime no cache
      // do MESMO board durante o voo do RPC.
      const preFetchCache = queryClient.getQueryData<DealView[]>(DEALS_BY_BOARD_KEY(boardId)) ?? [];
      const rpcIds = new Set(rpcResult.map(d => d.id));
      const realtimeOnly = preFetchCache.filter(
        d => !d.id.startsWith('temp-') && !rpcIds.has(d.id) && d.boardId === boardId
      );
      return realtimeOnly.length > 0 ? [...rpcResult, ...realtimeOnly] : rpcResult;
    },
    staleTime: 2 * 60 * 1000,
    // Gate na SESSAO real (access_token), nao so no `user` do contexto: evita
    // disparar a RPC antes do JWT estar anexado ao cliente supabase-js. Sem token,
    // a RPC roda com auth.uid()=NULL e (pos-migration) levanta erro -> board vazio.
    enabled:
      !authLoading && !!user && !!session?.access_token && !!boardId && !boardId.startsWith('temp-'),
  });
};

// ============ MUTATION HOOKS ============

// Input type for creating a deal (without auto-generated fields)
// isWon and isLost are optional and default to false
export type CreateDealInput = Omit<Deal, 'id' | 'createdAt' | 'updatedAt' | 'isWon' | 'isLost'> & {
  isWon?: boolean;
  isLost?: boolean;
};

/**
 * Hook to create a new deal
 */
export const useCreateDeal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deal: CreateDealInput) => {
      // organization_id will be auto-set by trigger on server
      const fullDeal = {
        ...deal,
        isWon: deal.isWon ?? false,
        isLost: deal.isLost ?? false,
        updatedAt: new Date().toISOString(),
      };

      // #region agent log
      if (process.env.NODE_ENV !== 'production') {
        const logData = { title: deal.title, status: deal.status?.slice(0, 8) || 'null' };
        console.log(`[useCreateDeal] 📤 Sending create to server`, logData);
      }
      // #endregion

      // Passa null ao invés de '' - o trigger vai preencher automaticamente
      const { data, error } = await dealsService.create(fullDeal);

      if (error) throw error;
      
      // #region agent log
      if (process.env.NODE_ENV !== 'production') {
        const logData = { dealId: data?.id?.slice(0, 8) || 'null', title: data?.title };
        console.log(`[useCreateDeal] ✅ Server confirmed creation`, logData);
      }
      // #endregion
      
      return data!;
    },
    onMutate: async newDeal => {
      // Snapshot pra rollback em caso de erro (cobre full + per-board)
      const snapshot = snapshotDealCaches(queryClient);

      // Optimistic insert com temp ID
      const tempId = `temp-${Date.now()}`;
      const tempDealView: DealView = {
        ...newDeal,
        id: tempId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isWon: newDeal.isWon ?? false,
        isLost: newDeal.isLost ?? false,
        companyName: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        stageLabel: '',
      } as DealView;

      if (process.env.NODE_ENV !== 'production') {
        console.log(`[useCreateDeal] 🔄 Optimistic insert with temp ID`, {
          tempId: tempId.slice(0, 15),
          title: newDeal.title,
          boardId: newDeal.boardId?.slice(0, 8) || 'null',
        });
      }

      insertDealInCaches(queryClient, tempDealView);

      return { snapshot, tempId };
    },
    onSuccess: (data, _variables, context) => {
      const tempId = context?.tempId;

      if (process.env.NODE_ENV !== 'production') {
        console.log(`[useCreateDeal] 🔄 Replacing temp deal with real one`, {
          tempId: tempId?.slice(0, 15) || 'null',
          realId: data.id?.slice(0, 8) || 'null',
          title: data.title,
        });
      }

      // Converte Deal pra DealView parcial (Realtime vai enriquecer depois)
      const dealAsView: DealView = {
        ...data,
        companyName: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        stageLabel: '',
      } as DealView;

      // Aplica swap temp->real em TODOS os caches (full + per-board)
      applyDealPatchToCaches(queryClient, (old = []) => {
        // Se o deal real já existe (Realtime nos venceu), mantém o existente
        if (old.some(d => d.id === data.id)) {
          if (process.env.NODE_ENV !== 'production') {
            console.log(`[useCreateDeal] ⚠️ Deal already exists (Realtime beat us)`, {
              dealId: data.id?.slice(0, 8),
            });
          }
          // Remove o temp se ainda estiver lá
          return tempId ? old.filter(d => d.id !== tempId) : old;
        }
        if (tempId) {
          const withoutTemp = old.filter(d => d.id !== tempId);
          return [dealAsView, ...withoutTemp];
        }
        return [dealAsView, ...old];
      });
    },
    onError: (_error, _newDeal, context) => {
      if (context?.snapshot) {
        restoreDealCachesSnapshot(queryClient, context.snapshot);
      }
    },
    onSettled: () => {
      // NÃO fazer invalidateQueries para deals - Realtime gerencia a sincronização
      // Isso evita race conditions onde o refetch sobrescreve o cache otimista
      // Apenas atualiza stats do dashboard
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
    },
  });
};

/**
 * Hook to update a deal
 * Usa DEALS_VIEW_KEY como única fonte de verdade
 */
export const useUpdateDeal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Deal> }) => {
      const { error } = await dealsService.update(id, updates);
      if (error) throw error;
      return { id, updates };
    },
    onMutate: async ({ id, updates }) => {
      const snapshot = snapshotDealCaches(queryClient);

      applyDealUpdateToCaches(queryClient, id, updates);

      // Detail cache (separado)
      queryClient.setQueryData<Deal>(queryKeys.deals.detail(id), old =>
        old ? { ...old, ...updates, updatedAt: new Date().toISOString() } : old
      );

      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) {
        restoreDealCachesSnapshot(queryClient, context.snapshot);
      }
    },
    onSettled: (_data, _error, { id }) => {
      // NÃO fazer invalidateQueries para deals - Realtime gerencia a sincronização
      // Apenas invalidar o detalhe específico se necessário
      queryClient.invalidateQueries({ queryKey: queryKeys.deals.detail(id) });
    },
  });
};

/**
 * Hook to update deal status (for drag & drop in Kanban)
 * @deprecated Use useMoveDeal instead - this hook is not used anywhere
 */
export const useUpdateDealStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      lossReason,
      isWon,
      isLost,
    }: {
      id: string;
      status: string;
      lossReason?: string;
      isWon?: boolean;
      isLost?: boolean;
    }) => {
      const updates: Partial<Deal> = {
        status,
        lastStageChangeDate: new Date().toISOString(),
        ...(lossReason && { lossReason }),
      };

      if (isWon !== undefined) {
        updates.isWon = isWon;
        if (isWon) updates.closedAt = new Date().toISOString();
      }
      if (isLost !== undefined) {
        updates.isLost = isLost;
        if (isLost) updates.closedAt = new Date().toISOString();
      }
      if (isWon === false && isLost === false) {
        updates.closedAt = null as unknown as string;
      }

      const { error } = await dealsService.update(id, updates);
      if (error) throw error;
      return { id, status, lossReason, isWon, isLost };
    },
    onMutate: async ({ id, status, lossReason, isWon, isLost }) => {
      const snapshot = snapshotDealCaches(queryClient);

      applyDealPatchToCaches(queryClient, (old = []) =>
        old.map(deal =>
          deal.id === id
            ? {
              ...deal,
              status,
              lastStageChangeDate: new Date().toISOString(),
              ...(lossReason && { lossReason }),
              ...(isWon !== undefined && { isWon }),
              ...(isLost !== undefined && { isLost }),
            }
            : deal
        )
      );

      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) {
        restoreDealCachesSnapshot(queryClient, context.snapshot);
      }
    },
    onSettled: () => {
      // NÃO fazer invalidateQueries - Realtime gerencia a sincronização
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
    },
  });
};

/**
 * Hook to delete a deal
 */
export const useDeleteDeal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await dealsService.delete(id);
      if (error) throw error;
      return id;
    },
    onMutate: async id => {
      // Snapshot pra rollback (cobre full + per-board)
      const snapshot = snapshotDealCaches(queryClient);

      // Optimistic remove em todos os caches. O helper já é defensivo:
      // só patcha caches com data definida (evita criar cache vazio).
      applyDealPatchToCaches(queryClient, (old = []) =>
        old.filter(deal => deal.id !== id)
      );

      return { snapshot };
    },
    onError: (_error, _id, context) => {
      if (context?.snapshot) {
        restoreDealCachesSnapshot(queryClient, context.snapshot);
      }
    },
    onSettled: () => {
      // Invalidate stats; the Realtime DELETE event handles removing the deal from cache.
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
    },
  });
};

// ============ DEAL ITEMS MUTATIONS ============

/**
 * Hook to add an item to a deal
 */
export const useAddDealItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dealId, item }: { dealId: string; item: Omit<DealItem, 'id'> }) => {
      const { data, error } = await dealsService.addItem(dealId, item);
      if (error) throw error;
      return { dealId, item: data! };
    },
    onSettled: (_data, _error, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deals.detail(dealId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.deals.lists() });
    },
  });
};

/**
 * Hook to remove an item from a deal
 */
export const useRemoveDealItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dealId, itemId }: { dealId: string; itemId: string }) => {
      const { error } = await dealsService.removeItem(dealId, itemId);
      if (error) throw error;
      return { dealId, itemId };
    },
    onSettled: (_data, _error, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deals.detail(dealId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.deals.lists() });
    },
  });
};

// ============ UTILITY HOOKS ============

/**
 * Hook to invalidate all deals queries (useful after bulk operations)
 */
export const useInvalidateDeals = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.deals.all });
};

/**
 * Hook to prefetch a deal (for hover previews)
 */
export const usePrefetchDeal = () => {
  const queryClient = useQueryClient();
  return async (id: string) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.deals.detail(id),
      queryFn: async () => {
        const { data, error } = await dealsService.getById(id);
        if (error) throw error;
        return data;
      },
      staleTime: 5 * 60 * 1000,
    });
  };
};

/**
 * Hook to create a deal together with an optional contact and/or company.
 * Performs an optimistic insert into DEALS_VIEW_KEY cache and replaces the
 * temporary entry with the real server response on success.
 */
export const useCreateDealWithContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      deal,
      relatedData,
    }: {
      deal: Omit<Deal, 'id' | 'createdAt'>;
      relatedData?: { contact?: Partial<Contact>; companyName?: string };
    }) => {
      let finalCompanyId = deal.companyId;
      let finalContactId = deal.contactId;

      // Create company if name provided
      if (relatedData?.companyName) {
        const { data: company, error: companyError } = await companiesService.create({ name: relatedData.companyName });
        if (companyError) throw companyError;
        if (company) finalCompanyId = company.id;
      }

      // Create contact if provided
      if (relatedData?.contact?.name) {
        const { data: contact, error: contactError } = await contactsService.create({
          name: relatedData.contact.name,
          email: relatedData.contact.email || '',
          phone: relatedData.contact.phone || '',
          companyId: finalCompanyId,
          status: 'ACTIVE',
          stage: 'LEAD',
        });
        if (contactError) throw contactError;
        if (contact) finalContactId = contact.id;
      }

      const { data: createdDeal, error } = await dealsService.create({
        ...deal,
        companyId: finalCompanyId,
        contactId: finalContactId,
      });
      if (error) throw error;
      return createdDeal!;
    },
    onMutate: async ({ deal, relatedData }) => {
      const snapshot = snapshotDealCaches(queryClient);
      const tempId = `temp-${Date.now()}`;
      const tempDeal: DealView = {
        ...(deal as any),
        id: tempId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        contactName: relatedData?.contact?.name || 'Sem contato',
        contactEmail: relatedData?.contact?.email || '',
        contactPhone: '',
        companyName: relatedData?.companyName || 'Sem empresa',
        clientCompanyName: relatedData?.companyName || 'Sem empresa',
        stageLabel: 'Novo',
        isWon: false,
        isLost: false,
      } as DealView;
      insertDealInCaches(queryClient, tempDeal);
      return { snapshot, tempId };
    },
    onSuccess: (data, _vars, context) => {
      const { tempId } = context as { tempId: string };
      const dealAsView: DealView = {
        ...data,
        companyName: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        stageLabel: '',
      } as DealView;
      applyDealPatchToCaches(queryClient, (old = []) => {
        if (old.some(d => d.id === data.id)) {
          return tempId ? old.filter(d => d.id !== tempId) : old;
        }
        const withoutTemp = old.filter(d => d.id !== tempId);
        return [dealAsView, ...withoutTemp];
      });
    },
    onError: (_err, _vars, context) => {
      const snapshot = (context as any)?.snapshot as ReturnType<typeof snapshotDealCaches> | undefined;
      if (snapshot) {
        restoreDealCachesSnapshot(queryClient, snapshot);
      }
    },
    onSettled: () => {
      // NÃO invalidar deals — Realtime gerencia sincronização (mesmo padrão do useCreateDeal)
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
    },
  });
};
